import { Fragment, memo } from 'react';
import { ActivityIndicator, View } from 'react-native';
import StyleSheet from 'react-native-media-query';
import { Divider, Icon, Text, TextProps } from '@ui-kitten/components';
import lodashIsEmpty from 'lodash/isEmpty';

import Button from '../../../Components/Button';
import { textCategory } from '../../../Components/Reservation/constants';
import ResponsiveText from '../../../Components/ResponsiveText';
import themeColor from '../../../Theme/colors';
import themeStyle from '../../../Theme/styles';
import breakpoints from '../../../Theme/styles/breakpoints';
import {
  ids as platformStyleIds,
  styles as platformStyles,
} from '../../../Theme/styles/platform';

const OptionBox = ({
  category = textCategory.SMALL_BODY,
  iconSize = 24,
  isLoading,
  label,
  placeholder,
  value,
  withBorder = true,
  onPress,
}: {
  category?: TextProps['category'];
  iconSize?: number;
  isLoading?: boolean;
  label: string;
  placeholder?: string;
  value?: { label: string; value: any };
  withBorder?: boolean;
  onPress?: () => void;
}) => {
  const isDropdown = typeof onPress === 'function';
  const usePlaceholder = lodashIsEmpty(value) && Boolean(placeholder);
  const Container = isDropdown ? Button : View;
  return (
    <Fragment>
      <Container
        dataSet={{ media: ids.container }}
        disabled={isLoading}
        onPress={onPress}
        style={styles.container}
        plain
      >
        <View
          dataSet={withBorder && { media: ids.boxBorder }}
          style={[
            themeStyle.flexDirectionRowCenter,
            withBorder && styles.boxBorder,
          ]}
        >
          <View style={themeStyle.flex1}>
            <Text
              category={category}
              dataSet={{ media: ids.label }}
              numberOfLines={1}
              status="info"
              style={styles.label}
            >
              {!usePlaceholder ? placeholder || label : label}
            </Text>
            <ResponsiveText category={category} numberOfLines={1}>
              {usePlaceholder ? placeholder : value?.label}
            </ResponsiveText>
          </View>
          {isLoading ? (
            <ActivityIndicator color={themeColor.black} size={iconSize} />
          ) : (
            <Icon
              name="chevron-down"
              fill={themeColor.black}
              style={[
                { width: iconSize, height: iconSize },
                themeStyle.spacingLeftMedium,
              ]}
            />
          )}
        </View>
      </Container>
      {withBorder && (
        <Divider
          dataSet={{ media: platformStyleIds.webOnly }}
          style={[themeStyle.divider, platformStyles.webOnly]}
        />
      )}
    </Fragment>
  );
};

const { ids, styles } = StyleSheet.create({
  container: {
    ...themeStyle.flex1,
    [`@media ${breakpoints.xs}`]: themeStyle.pageVerticalSpacingSmall,
    [`@media ${breakpoints.lg}`]: themeStyle.pageVerticalSpacingMedium,
  },
  boxBorder: {
    ...themeStyle.pageVerticalSpacingSmall,
    ...themeStyle.pageHorizontalSpacingSmall,
    borderWidth: 1,
    borderColor: themeColor.gray,
    [`@media ${breakpoints.lg}`]: {
      borderWidth: 0,
      padding: 0,
    },
  },
  label: {
    [`@media ${breakpoints.xs}`]: {
      fontSize: 10,
    },
  },
});

export default memo(OptionBox);
